/**
 * SourceGroupNode — Custom ReactFlow node for source tables on the pipeline canvas.
 *
 * When sources come from a single origin, renders as a generic "Source Data" node.
 * When sources come from multiple origins (file uploads + connectors), the pipeline
 * builder creates SEPARATE SourceGroupNode instances per origin. Each node receives
 * `originLabel` and `connectorType` to render with the correct title and icon.
 *
 * Visual spec:
 * - Min width 260px (grows to fit longest table name), light background, indigo left border
 * - Header: origin icon + origin label + table count + "+" add button (menu)
 * - Compact table list: table_name  rows x cols (with hover replace icon)
 * - Bottom Handle only (source -> transforms)
 */
import React, { useState } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, IconButton, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import {
  Storage as StorageIcon,
  NoteAdd as AddIcon,
  SwapHoriz as ReplaceIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

const SHOW_REPLACE_BUTTON = false;

export interface SourceGroupNodeData {
  tableCount: number;
  totalRowCount: number;
  tables: Array<{ name: string; displayName?: string; rowCount: number; columnCount: number }>;
  /** 'completed' when tables are loaded, 'pending' while loading */
  status?: 'completed' | 'pending';
  /** Per-origin label (e.g. "File Uploads", "CRO Database"). Absent for generic single-origin nodes. */
  originLabel?: string;
  /** Connector type for per-origin icon (e.g. "postgres"). null for file uploads. Absent for generic nodes. */
  connectorType?: string | null;
  /** Callback to add a new source table via file upload. */
  onAddSource?: () => void;
  /** Callback to add a new source table from a connector. */
  onAddFromConnector?: () => void;
  /** Callback to replace a specific table (opens file picker for that table).
   *  Receives the clicked table name AND all table names in this source group
   *  so the backend can clean up all sibling sheet tables on an Excel→CSV replace. */
  onReplaceTable?: (tableName: string, groupTableNames: string[]) => void;
  /** Callback when user clicks a table row to open data preview in inspector. */
  onTableClick?: (tableName: string) => void;
}

const MAX_TABLES_SHOWN = 4;

/** Strip the connector prefix (e.g. "pg-prod::customers" -> "customers") for display. */
function stripPrefix(name: string): string {
  const sep = name.indexOf('::');
  return sep >= 0 ? name.slice(sep + 2) : name;
}

/** Renders a single table row. */
const TableRow: React.FC<{
  table: { name: string; displayName?: string; rowCount: number; columnCount: number };
  /** Display name — may differ from table.name (e.g. strip connector prefix). */
  displayName?: string;
  onTableClick?: (tableName: string) => void;
  onReplaceTable?: (tableName: string) => void;
  primaryColor: string;
}> = ({ table: t, displayName, onTableClick, onReplaceTable, primaryColor }) => ( // onReplaceTable here is pre-bound with group names by parent
  <Box
    onClick={(e) => { e.stopPropagation(); onTableClick?.(t.name); }}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.25,
      cursor: 'pointer',
      borderRadius: 0.5,
      px: 0.5,
      mx: -0.5,
      '&:hover': { bgcolor: alpha(primaryColor, 0.04) },
      '&:hover .replace-btn': { opacity: 1 },
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontFamily: 'monospace',
        color: '#334155',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        mr: 1,
      }}
    >
      {displayName ?? t.name}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {SHOW_REPLACE_BUTTON && onReplaceTable && (
        <Tooltip title={`Replace ${displayName ?? t.name}`}>
          <IconButton
            className="replace-btn"
            size="small"
            onClick={(e) => { e.stopPropagation(); onReplaceTable(t.name); }}
            sx={{
              p: 0,
              opacity: 0,
              transition: 'opacity 0.15s',
              color: '#64748b',
              '&:hover': { color: primaryColor },
            }}
          >
            <ReplaceIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      )}
      <Typography sx={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
        {t.rowCount.toLocaleString()} × {t.columnCount}
      </Typography>
    </Box>
  </Box>
);

const SourceGroupNode: React.FC<NodeProps<SourceGroupNodeData>> = ({
  data,
  selected,
  sourcePosition,
}) => {
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const theme = useTheme();
  const { tableCount, totalRowCount, tables, status, originLabel, connectorType, onAddSource, onAddFromConnector, onReplaceTable, onTableClick } = data;
  const isLoading = status === 'pending' || (tableCount === 0 && tables.length === 0);
  const [expanded, setExpanded] = useState(false);

  // Per-origin nodes strip connector prefixes from table names for cleaner display
  const isPerOrigin = !!originLabel;

  // Choose header icon based on connector type
  const HeaderIcon = isPerOrigin
    ? (connectorType ? StorageIcon : StorageIcon)
    : StorageIcon;

  // Title: origin label for per-origin nodes, "Source Data" for generic
  const title = originLabel ?? 'Source Data';

  // Single handler: prefer file-upload callback, fall back to connector
  const handleAddSource = onAddSource ?? onAddFromConnector;

  const visibleTables = expanded ? tables : tables.slice(0, MAX_TABLES_SHOWN);
  const remainingCount = tables.length - MAX_TABLES_SHOWN;

  return (
    <Box
      sx={{
        minWidth: 260,
        width: 'max-content',
        background: '#f8fafc',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${theme.palette.primary.main}`
          : '1.5px solid #e2e8f0',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`
          : '0 1px 3px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <HeaderIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ fontSize: 13, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 11 }}
          >
            {isLoading
              ? 'Loading sources\u2026'
              : `${tableCount} table${tableCount !== 1 ? 's' : ''} \u00b7 ${totalRowCount.toLocaleString()} rows`}
          </Typography>
        </Box>
        {/* Add source button — opens the Add Data Source dialog directly */}
        {handleAddSource && !isLoading && (
          <Tooltip title="Add source table">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleAddSource(); }}
              sx={{
                p: 0.25,
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {/* Status indicator */}
        {isLoading ? (
          <CircularProgress size={12} sx={{ flexShrink: 0 }} />
        ) : (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#22c55e',
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {/* Table list */}
      <Box sx={{ px: 1.5, py: 0.75, minHeight: 24 }}>
        {isLoading && (
          <Typography sx={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
            {'Auto-loading source files\u2026'}
          </Typography>
        )}
        {!isLoading && (
          <>
            {visibleTables.map((t) => (
              <TableRow
                key={t.name}
                table={t}
                displayName={t.displayName ?? (isPerOrigin ? stripPrefix(t.name) : undefined)}
                onTableClick={onTableClick}
                onReplaceTable={onReplaceTable ? (name) => {
                  // Replace only the clicked table — never auto-include siblings.
                  // If the user clicks replace on one Excel sheet, only that sheet
                  // should be replaced; the other sheets stay untouched.
                  onReplaceTable(name, [name]);
                } : undefined}
                primaryColor={theme.palette.primary.main}
              />
            ))}
            {remainingCount > 0 && !expanded && (
              <Typography
                onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                sx={{
                  fontSize: 10,
                  color: theme.palette.primary.main,
                  mt: 0.25,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                + {remainingCount} more
              </Typography>
            )}
            {expanded && remainingCount > 0 && (
              <Typography
                onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                sx={{
                  fontSize: 10,
                  color: theme.palette.primary.main,
                  mt: 0.25,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Show less
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Source handle — edges to transforms */}
      <Handle
        type="source"
        position={sourceHandlePos}
        style={{
          background: theme.palette.primary.main,
          width: 8,
          height: 8,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      />
    </Box>
  );
};

export default SourceGroupNode;
